/**
 * API测试面板组件
 * 用于测试后端API连接和功能
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Input,
  message,
} from 'antd';
import {
  ApiOutlined,
  HeartOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  useGetHealthQuery,
  useGetApiStatusQuery,
  useTestApiMutation,
} from '../../store/api/apiSlice';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export const ApiTestPanel: React.FC = () => {
  const { t } = useTranslation();
  const [testMessage, setTestMessage] = useState('Hello from Redux frontend!');

  // API查询hooks
  const {
    data: healthData,
    error: healthError,
    isLoading: healthLoading,
    refetch: refetchHealth,
  } = useGetHealthQuery();

  const {
    data: statusData,
    error: statusError,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useGetApiStatusQuery();

  const [
    testApi,
    { data: testData, error: testError, isLoading: testLoading },
  ] = useTestApiMutation();

  // 处理测试API调用
  const handleTestApi = async () => {
    try {
      const result = await testApi({
        message: testMessage,
        timestamp: new Date().toISOString(),
        source: 'Redux Frontend',
        userAgent: navigator.userAgent,
      }).unwrap();

      message.success('API测试成功！');
      console.log('API Test Result:', result);
    } catch (error) {
      message.error('API测试失败');
      console.error('API Test Error:', error);
    }
  };

  // 渲染状态指示器
  const renderStatusIndicator = (loading: boolean, error: any, data: any) => {
    if (loading) {
      return <Spin size="small" />;
    }

    if (error) {
      return (
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <Text type="danger">连接失败</Text>
        </Space>
      );
    }

    if (data?.success) {
      return (
        <Space>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <Text type="success">连接正常</Text>
        </Space>
      );
    }

    return (
      <Space>
        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
        <Text type="warning">{t('common:status.unknown')}</Text>
      </Space>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Title level={2}>
        <ApiOutlined /> API连接测试面板
      </Title>

      <Paragraph>
        这个面板用于测试前端与后端API的连接状态。确保后端服务正在运行
        (http://localhost:3001)。
      </Paragraph>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 健康检查 */}
        <Card
          title={
            <Space>
              <HeartOutlined />
              <span>健康检查</span>
              {renderStatusIndicator(healthLoading, healthError, healthData)}
            </Space>
          }
          extra={
            <Button
              onClick={() => refetchHealth()}
              loading={healthLoading}
              size="small"
            >
              刷新
            </Button>
          }
        >
          {healthError && (
            <Alert
              message="健康检查失败"
              description={`错误信息: ${JSON.stringify(healthError)}`}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {healthData?.success && (
            <div>
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                <Text>
                  <strong>状态:</strong> {healthData.data.status}
                </Text>
                <Text>
                  <strong>环境:</strong> {healthData.data.environment}
                </Text>
                <Text>
                  <strong>版本:</strong> {healthData.data.version}
                </Text>
                <Text>
                  <strong>运行时间:</strong>{' '}
                  {Math.round(healthData.data.uptime)}秒
                </Text>
                <Text>
                  <strong>时间戳:</strong>{' '}
                  {new Date(healthData.data.timestamp).toLocaleString()}
                </Text>
              </Space>
            </div>
          )}
        </Card>

        {/* API状态检查 */}
        <Card
          title={
            <Space>
              <ApiOutlined />
              <span>API状态</span>
              {renderStatusIndicator(statusLoading, statusError, statusData)}
            </Space>
          }
          extra={
            <Button
              onClick={() => refetchStatus()}
              loading={statusLoading}
              size="small"
            >
              刷新
            </Button>
          }
        >
          {statusError && (
            <Alert
              message="API状态检查失败"
              description={`错误信息: ${JSON.stringify(statusError)}`}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {statusData?.success && (
            <div>
              <Text>
                <strong>可用端点:</strong>
              </Text>
              <ul style={{ marginTop: '8px' }}>
                {statusData.data.endpoints.map((endpoint, index) => (
                  <li key={index}>
                    <Text code>{endpoint}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        {/* API测试 */}
        <Card
          title={
            <Space>
              <SendOutlined />
              <span>API测试</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>测试消息:</Text>
              <TextArea
                value={testMessage}
                onChange={e => setTestMessage(e.target.value)}
                placeholder="输入要发送的测试消息"
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={{ marginTop: '8px' }}
              />
            </div>

            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleTestApi}
              loading={testLoading}
              disabled={!testMessage.trim()}
            >
              发送测试请求
            </Button>

            {testError && (
              <Alert
                message="API测试失败"
                description={`错误信息: ${JSON.stringify(testError)}`}
                type="error"
                showIcon
              />
            )}

            {testData?.success && (
              <Alert
                message="API测试成功"
                description={
                  <div>
                    <p>
                      <strong>发送的消息:</strong>{' '}
                      {testData.data.received.message}
                    </p>
                    <p>
                      <strong>服务器时间:</strong>{' '}
                      {new Date(testData.data.timestamp).toLocaleString()}
                    </p>
                    <p>
                      <strong>原始响应:</strong>
                    </p>
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: '8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        maxHeight: '200px',
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(testData, null, 2)}
                    </pre>
                  </div>
                }
                type="success"
                showIcon
              />
            )}
          </Space>
        </Card>

        {/* 连接信息 */}
        <Card title="连接信息">
          <Space direction="vertical" size="small">
            <Text>
              <strong>后端地址:</strong>{' '}
              {process.env.REACT_APP_API_URL || 'http://localhost:3001'}
            </Text>
            <Text>
              <strong>前端地址:</strong> {window.location.origin}
            </Text>
            <Text>
              <strong>Redux状态:</strong> 已连接
            </Text>
            <Text>
              <strong>RTK Query:</strong> 已启用
            </Text>
          </Space>
        </Card>
      </Space>
    </div>
  );
};
